import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout.jsx';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { FileText, Search, Filter, Trash2, Eye, Calendar, AlertCircle, Share2, Link2, Copy, Clock, Download } from 'lucide-react';
import { getHealthRecords, deleteHealthRecord, getFamilyMembers, createShareLink } from '@/services/api';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

const recordTypeLabels = {
  blood_test: 'Blood Test',
  imaging: 'Imaging',
  checkup: 'Health Checkup',
  diabetes: 'Diabetes Panel',
  thyroid: 'Thyroid Test',
  liver: 'Liver Function',
  kidney: 'Kidney Function',
  cardiac: 'Cardiac Tests',
  other: 'Other'
};

function RecordsPage() {
  const [records, setRecords] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterMember, setFilterMember] = useState('all');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [shareDuration, setShareDuration] = useState(7);
  const [shareUnit, setShareUnit] = useState('days');
  const [shareLink, setShareLink] = useState('');
  const [shareExpiresAt, setShareExpiresAt] = useState('');
  const [shareLoading, setShareLoading] = useState(false);

  useEffect(function() {
    loadData();
  }, []);

  async function loadData() {
    try {
      const recordsData = await getHealthRecords();
      const membersData = await getFamilyMembers();
      setRecords(recordsData);
      setFamilyMembers(membersData);
    } catch (error) {
      toast.error('Failed to load records');
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(recordId) {
    if (!window.confirm('Delete this record?')) return;
    try {
      await deleteHealthRecord(recordId);
      setRecords(records.filter(function(r) { return r.id !== recordId; }));
      toast.success('Record deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  }

  function getFilteredRecords() {
    return records.filter(function(record) {
      const title = record.title || '';
      const matchesSearch = title.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1;
      const matchesType = filterType === 'all' || record.record_type === filterType;
      const matchesMember = filterMember === 'all' || record.user_id === filterMember;
      return matchesSearch && matchesType && matchesMember;
    });
  }

  function getMemberName(userId) {
    for (let i = 0; i < familyMembers.length; i++) {
      if (familyMembers[i].user_id === userId) {
        return familyMembers[i].full_name;
      }
    }
    return 'You';
  }

  function getAbnormalCount(record) {
    if (!record) return 0;
    const analysis = record.analysis;
    if (!analysis) return 0;
    const findings = analysis.abnormal_findings;
    if (!findings) return 0;
    return findings.length;
  }

  function getMetrics(record) {
    if (!record) return [];
    const analysis = record.analysis;
    if (!analysis) return [];
    return analysis.metrics || [];
  }

  function getInsights(record) {
    if (!record) return [];
    const analysis = record.analysis;
    if (!analysis) return [];
    return analysis.insights || [];
  }

  function getRecommendations(record) {
    if (!record) return [];
    const analysis = record.analysis;
    if (!analysis) return [];
    return analysis.recommendations || [];
  }

  function getAbnormalFindings(record) {
    if (!record) return [];
    const analysis = record.analysis;
    if (!analysis) return [];
    return analysis.abnormal_findings || [];
  }

  function viewRecordDetails(record) {
    setSelectedRecord(record);
    setDetailsOpen(true);
    setShareDuration(7);
    setShareUnit('days');
    setShareLink('');
    setShareExpiresAt('');
  }

  function getShareHours() {
    const durationValue = Number(shareDuration);
    if (!durationValue || durationValue <= 0) return null;
    return shareUnit === 'days' ? durationValue * 24 : durationValue;
  }

  async function handleShareLinkCreate() {
    if (!selectedRecord) return;
    const shareHours = getShareHours();
    if (!shareHours || shareHours < 24 || shareHours > 720) {
      toast.error('Select a duration between 24 hours and 30 days');
      return;
    }

    setShareLoading(true);
    setShareLink('');
    setShareExpiresAt('');

    try {
      const result = await createShareLink(selectedRecord.id, shareHours);
      const shareUrl = `${window.location.origin}/share/${result.token}`;
      setShareLink(shareUrl);
      setShareExpiresAt(result.expires_at);
      toast.success('Share link generated');
    } catch (error) {
      toast.error('Failed to generate share link');
    } finally {
      setShareLoading(false);
    }
  }

  async function handleCopyShareLink() {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      toast.success('Share link copied');
    } catch (error) {
      toast.error('Unable to copy link');
    }
  }

  function formatMetricLine(metric) {
    const base = `${metric.name}: ${metric.value} ${metric.unit || ''}`.trim();
    if (metric.normal_range) {
      return `${base} (Normal: ${metric.normal_range})`;
    }
    return base;
  }

  function addSection(doc, title, contentLines, yPosition) {
    let y = yPosition;
    doc.setFont('helvetica', 'bold');
    doc.text(title, 40, y);
    y += 16;
    doc.setFont('helvetica', 'normal');
    contentLines.forEach((line) => {
      const splitLines = doc.splitTextToSize(line, 520);
      splitLines.forEach((splitLine) => {
        if (y > 760) {
          doc.addPage();
          y = 40;
        }
        doc.text(splitLine, 40, y);
        y += 14;
      });
    });
    return y + 8;
  }

  function handleExportPdf() {
    if (!selectedRecord) return;

    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    let y = 40;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text('LYFSYNC Health Record', 40, y);
    y += 24;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.text(`Record: ${selectedRecord.title || 'Health Record'}`, 40, y);
    y += 16;
    doc.text(`Type: ${recordTypeLabels[selectedRecord.record_type] || selectedRecord.record_type}`, 40, y);
    y += 16;
    doc.text(`Date: ${getRecordDate(selectedRecord)}`, 40, y);
    y += 16;
    doc.text(`Member: ${getMemberName(selectedRecord.user_id)}`, 40, y);
    y += 20;

    if (selectedRecord.notes) {
      y = addSection(doc, 'Notes', [selectedRecord.notes], y);
    }

    const metrics = getMetrics(selectedRecord).map(formatMetricLine);
    if (metrics.length) {
      y = addSection(doc, 'Metrics', metrics, y);
    }

    const findings = getAbnormalFindings(selectedRecord);
    if (findings.length) {
      y = addSection(doc, 'Abnormal Findings', findings, y);
    }

    const insights = getInsights(selectedRecord);
    if (insights.length) {
      y = addSection(doc, 'Insights', insights, y);
    }

    const recommendations = getRecommendations(selectedRecord);
    if (recommendations.length) {
      y = addSection(doc, 'Recommendations', recommendations, y);
    }

    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated on ${new Date().toLocaleString()}`, 40, 800);

    const fileName = `${(selectedRecord.title || 'health-record').replace(/[^a-z0-9-_]+/gi, '_').toLowerCase()}.pdf`;
    doc.save(fileName);
    toast.success('PDF exported');
  }

  function getRecordDate(record) {
    if (record.test_date) return record.test_date;
    if (record.created_at) return record.created_at.split('T')[0];
    return '';
  }

  const filteredRecords = getFilteredRecords();

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="records-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Health Records
            </h1>
            <p className="text-slate-600 mt-1">View and manage all your medical records</p>
          </div>
        </div>

        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search records..."
                  value={searchTerm}
                  onChange={function(e) { setSearchTerm(e.target.value); }}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-full md:w-48" data-testid="filter-type-select">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Record Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="filter-type-option-all">All Types</SelectItem>
                  <SelectItem value="blood_test" data-testid="filter-type-option-blood">Blood Test</SelectItem>
                  <SelectItem value="imaging" data-testid="filter-type-option-imaging">Imaging</SelectItem>
                  <SelectItem value="checkup" data-testid="filter-type-option-checkup">Health Checkup</SelectItem>
                  <SelectItem value="diabetes" data-testid="filter-type-option-diabetes">Diabetes Panel</SelectItem>
                  <SelectItem value="thyroid" data-testid="filter-type-option-thyroid">Thyroid Test</SelectItem>
                  <SelectItem value="liver" data-testid="filter-type-option-liver">Liver Function</SelectItem>
                  <SelectItem value="kidney" data-testid="filter-type-option-kidney">Kidney Function</SelectItem>
                  <SelectItem value="cardiac" data-testid="filter-type-option-cardiac">Cardiac Tests</SelectItem>
                  <SelectItem value="other" data-testid="filter-type-option-other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="space-y-4">
            <div className="skeleton h-24 rounded-xl"></div>
            <div className="skeleton h-24 rounded-xl"></div>
            <div className="skeleton h-24 rounded-xl"></div>
          </div>
        ) : filteredRecords.length === 0 ? (
          <Card className="bg-white border-slate-200 shadow-sm" data-testid="records-empty-state">
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 font-medium">No records found</p>
              <p className="text-sm text-slate-400 mt-1">Upload your first health record to get started</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredRecords.map(function(record, index) {
              const abnormalCount = getAbnormalCount(record);
              const hasAlerts = abnormalCount > 0;
              return (
                <Card 
                  key={record.id} 
                  className="bg-white border-slate-200 shadow-sm card-hover"
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4 min-w-0 flex-1">
                        <div className={hasAlerts ? "w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-red-100" : "w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-blue-100"}>
                          <FileText className={hasAlerts ? "w-6 h-6 text-red-600" : "w-6 h-6 text-blue-900"} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-slate-900 truncate">{record.title}</h3>
                            {hasAlerts && (
                              <span className="flex-shrink-0 px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                                {abnormalCount} Alert{abnormalCount > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                            <span className="inline-flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              {getRecordDate(record)}
                            </span>
                            <span className="px-2 py-0.5 bg-slate-100 rounded-full text-xs">
                              {recordTypeLabels[record.record_type] || record.record_type}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={function() { viewRecordDetails(record); }}
                          className="text-slate-400 hover:text-blue-600"
                          data-testid={`view-record-${record.id}-btn`}
                        >
                          <Eye className="w-5 h-5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={function() { handleDelete(record.id); }}
                          className="text-slate-400 hover:text-red-600"
                          data-testid={`delete-record-${record.id}-btn`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="record-details-dialog">
            {selectedRecord && (
              <div>
                <DialogHeader>
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                    <div>
                      <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {selectedRecord.title}
                      </DialogTitle>
                      <DialogDescription>
                        {recordTypeLabels[selectedRecord.record_type]} - {getRecordDate(selectedRecord)}
                      </DialogDescription>
                    </div>
                    <Button
                      variant="outline"
                      className="border-blue-200 text-blue-900"
                      onClick={handleExportPdf}
                      data-testid="export-record-pdf-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export PDF
                    </Button>
                  </div>
                </DialogHeader>

                <div className="space-y-6 mt-4">
                  {selectedRecord.notes && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">Notes</h4>
                      <p className="text-slate-700">{selectedRecord.notes}</p>
                    </div>
                  )}

                  {getMetrics(selectedRecord).length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Metrics</h4>
                      <div className="grid grid-cols-2 gap-3">
                        {getMetrics(selectedRecord).map(function(metric, index) {
                          const isHigh = metric.status === 'high' || metric.status === 'concerning';
                          const isLow = metric.status === 'low';
                          return (
                            <div key={index} className={isHigh ? "p-3 rounded-lg border bg-red-50 border-red-200" : isLow ? "p-3 rounded-lg border bg-amber-50 border-amber-200" : "p-3 rounded-lg border bg-slate-50 border-slate-200"}>
                              <p className="text-sm text-slate-600">{metric.name}</p>
                              <p className="text-lg font-bold text-slate-900">{metric.value} {metric.unit}</p>
                              {metric.normal_range && <p className="text-xs text-slate-500">Normal: {metric.normal_range}</p>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {getAbnormalFindings(selectedRecord).length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Abnormal Findings</h4>
                      <div className="space-y-2">
                        {getAbnormalFindings(selectedRecord).map(function(finding, index) {
                          return (
                            <div key={index} className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-lg">
                              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                              <p className="text-slate-800">{finding}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {getInsights(selectedRecord).length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Insights</h4>
                      <div className="space-y-2">
                        {getInsights(selectedRecord).map(function(insight, index) {
                          return (
                            <p key={index} className="text-slate-700 p-3 bg-sky-50 rounded-lg border-l-4 border-sky-500">
                              {insight}
                            </p>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {getRecommendations(selectedRecord).length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Recommendations</h4>
                      <ul className="space-y-2">
                        {getRecommendations(selectedRecord).map(function(rec, index) {
                          return (
                            <li key={index} className="text-slate-700 flex items-start gap-2">
                              <span className="text-green-600 mt-1">•</span>
                              {rec}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  <div className="border-t border-slate-200 pt-6" data-testid="share-section">
                    <div className="flex items-center gap-2 mb-2">
                      <Share2 className="w-4 h-4 text-blue-900" />
                      <h4 className="text-sm font-semibold text-slate-900 uppercase tracking-wide">Share with Doctor</h4>
                    </div>
                    <p className="text-sm text-slate-500 mb-4">
                      Generate a secure link that expires between 24 hours and 30 days.
                    </p>
                    <div className="grid gap-3 md:grid-cols-3 items-end">
                      <div className="md:col-span-1 space-y-2">
                        <Label htmlFor="shareDuration">Duration</Label>
                        <Input
                          id="shareDuration"
                          type="number"
                          min={shareUnit === 'days' ? 1 : 24}
                          max={shareUnit === 'days' ? 30 : 720}
                          value={shareDuration}
                          onChange={function(e) { setShareDuration(e.target.value); }}
                          data-testid="share-duration-input"
                        />
                      </div>
                      <div className="md:col-span-1 space-y-2">
                        <Label>Unit</Label>
                        <Select value={shareUnit} onValueChange={setShareUnit}>
                          <SelectTrigger data-testid="share-unit-select">
                            <Clock className="w-4 h-4 mr-2" />
                            <SelectValue placeholder="Select unit" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="hours" data-testid="share-unit-hours">Hours</SelectItem>
                            <SelectItem value="days" data-testid="share-unit-days">Days</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        className="bg-blue-900 hover:bg-blue-800 text-white"
                        onClick={handleShareLinkCreate}
                        disabled={shareLoading}
                        data-testid="generate-share-link-btn"
                      >
                        {shareLoading ? 'Generating...' : 'Generate Link'}
                      </Button>
                    </div>

                    {shareLink && (
                      <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50 p-4" data-testid="share-link-card">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 text-sm text-blue-900 font-semibold">
                              <Link2 className="w-4 h-4" />
                              Secure share link
                            </div>
                            <p className="text-xs text-blue-700 mt-1" data-testid="share-link-expiry">
                              Expires: {new Date(shareExpiresAt).toLocaleString()}
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-blue-200 text-blue-900"
                            onClick={handleCopyShareLink}
                            data-testid="copy-share-link-btn"
                          >
                            <Copy className="w-4 h-4 mr-2" />
                            Copy
                          </Button>
                        </div>
                        <Input
                          value={shareLink}
                          readOnly
                          className="mt-3 bg-white"
                          data-testid="share-link-input"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

export default RecordsPage;
